'use client'

import { Button, Popover, PopoverContent, PopoverTrigger } from '@yeshuman/sdk'
import { cloneElement, isValidElement, useCallback, useState, type MouseEvent, type ReactElement, type ReactNode } from 'react'
import { CircleAlert } from 'lucide-react'

import type { PublishBlocker } from '../../utils/opportunity-publish-readiness'

type PublishReadinessPeekProps = {
  blockers: PublishBlocker[]
  onFixDetails: () => void
  children: ReactElement
}

/**
 * Explains why Publish is blocked by missing job meta (employment type, work
 * mode, location, salary). Mirrors GatePeek's click-intercept popover pattern
 * but uses plain "finish your details" copy — not the promotion/upgrade
 * visual language of GatePeekPanel.
 */
export function PublishReadinessPeek({ blockers, onFixDetails, children }: PublishReadinessPeekProps) {
  const [open, setOpen] = useState(false)

  const handleFixDetails = useCallback(() => {
    setOpen(false)
    onFixDetails()
  }, [onFixDetails])

  if (blockers.length === 0) {
    return children
  }

  if (!isValidElement(children)) {
    return children as ReactNode
  }

  const interceptClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(true)
  }

  const gatedChild = cloneElement(children, {
    onClick: interceptClick,
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
  } as Partial<(typeof children)['props']>)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{gatedChild}</PopoverTrigger>
      <PopoverContent className="w-80" align="end" role="dialog" aria-label="Finish job details to publish">
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="space-y-1">
              <p className="text-sm font-medium leading-snug">Finish job details to publish</p>
              <p className="text-xs text-muted-foreground leading-snug">
                Publishing needs a few fields so candidates see a complete role.
              </p>
              <ul className="mt-2 space-y-1.5">
                {blockers.map((blocker) => (
                  <li
                    key={blocker.code + blocker.message}
                    className="flex items-start gap-1.5 text-xs text-muted-foreground leading-snug"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" aria-hidden />
                    {blocker.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Button type="button" size="sm" fullWidth onClick={handleFixDetails}>
            Edit job details
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
